import React, { useState } from 'react';
import { BarChart3, TrendingUp, X, ArrowRight } from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import './FinanceDashboard.css';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const fetchForecast = async ({ queryKey }) => {
  const [, year] = queryKey;
  const token = localStorage.getItem("tokenInv");
  const url = `${process.env.REACT_APP_URL}/api/dashboard-forecast?type=fy&year=${year}`;
  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });
  return response.data;
};

const CHARGE_TYPE_LABELS = {
  REC: { label: 'Recurring', color: '#3b82f6', bg: '#eff6ff' },
  NRC: { label: 'Non-Recurring', color: '#8b5cf6', bg: '#f5f3ff' },
  CE:  { label: 'Credit/Extension', color: '#10b981', bg: '#ecfdf5' },
  ADJ: { label: 'Adjustment', color: '#f59e0b', bg: '#fffbeb' },
  USG: { label: 'Usage', color: '#ec4899', bg: '#fdf2f8' },
};

const fmt = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtShort = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toFixed(2);
};

// ─── Detail Dialog ───────────────────────────────────────
const ForecastDetailDialog = ({ open, onClose, data, formatCurrency }) => {
  const [activeTab, setActiveTab] = useState('overview');
  if (!data || !open) return null;

  const chargeBreakdown = data.charge_type_breakdown || [];
  const months = data.months || [];


  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'charges', label: 'Charge Breakdown' },
    { id: 'months', label: 'Monthly Detail' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh' } }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
        <div>
          <div style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>Forecast Details</div>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{data.label || `FY ${data.year}`}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', color: '#94a3b8' }}>
          <X size={20} />
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 20px', background: '#fff' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 16px', fontSize: '12px', fontWeight: 700, border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === tab.id ? '#3b82f6' : '#94a3b8',
              transition: 'all 0.2s',
              textTransform: 'uppercase', letterSpacing: '0.05em'
            }}
          >{tab.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(90vh - 130px)' }}>

        {/* ── Overview Tab ── */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Full Year Forecast</div>
                <Tooltip title={`USD ${fmt(data.full_year_forecast)}`} arrow placement="top"><div style={{ fontSize: '20px', fontWeight: 800, color: '#1e40af', cursor: 'help' }}>USD {fmtShort(data.full_year_forecast)}</div></Tooltip>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Avg Monthly</div>
                <Tooltip title={`USD ${fmt(data.average_monthly)}`} arrow placement="top"><div style={{ fontSize: '20px', fontWeight: 800, color: '#15803d', cursor: 'help' }}>USD {fmtShort(data.average_monthly)}</div></Tooltip>
              </div>
            </div>

            {/* Actual vs Projected */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Actual ({data.actual_months} months)</div>
                <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e40af' }}>USD {fmt(data.actual_total)}</div>
              </div>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Projected ({data.projected_months} months)</div>
                <div style={{ fontSize: '18px', fontWeight: 800, color: '#92400e' }}>USD {fmt(data.projected_total)}</div>
              </div>
            </div>

            {/* Period Info */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '11px' }}>
              {data.last_actual_month && (
                <span style={{ background: '#eff6ff', color: '#2563eb', padding: '3px 10px', borderRadius: '12px', fontWeight: 600 }}>
                  Last Actual: {data.last_actual_month}
                </span>
              )}
              {data.as_of && (
                <span style={{ background: '#f5f3ff', color: '#7c3aed', padding: '3px 10px', borderRadius: '12px', fontWeight: 600 }}>
                  As of: {data.as_of}
                </span>
              )}
              {data.period_status && (
                <span style={{ background: data.period_status === 'in_progress' ? '#fefce8' : '#f0fdf4', color: data.period_status === 'in_progress' ? '#a16207' : '#15803d', padding: '3px 10px', borderRadius: '12px', fontWeight: 600, textTransform: 'capitalize' }}>
                  {data.period_status.replace('_', ' ')}
                </span>
              )}
              {data.no_invoice_months > 0 && (
                <span style={{ background: '#fef2f2', color: '#dc2626', padding: '3px 10px', borderRadius: '12px', fontWeight: 600 }}>
                  {data.no_invoice_months} months with no invoice
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Charge Breakdown Tab ── */}
        {activeTab === 'charges' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Forecast by Charge Type</div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Charge Type</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg Monthly (USD)</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Projected Total (USD)</th>

                  </tr>
                </thead>
                <tbody>
                  {chargeBreakdown.map((ct, i) => {
                    const config = CHARGE_TYPE_LABELS[ct.charge_type] || { label: ct.charge_type, color: '#64748b', bg: '#f8fafc' };
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: config.color, display: 'inline-block' }} />
                            <span style={{ fontWeight: 600, color: '#334155' }}>{ct.charge_type}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600, color: '#334155' }}>{fmt(ct.average_monthly)}</td>
                        <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 600, color: '#334155' }}>{fmt(ct.projected_total)}</td>

                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 800, color: '#0f172a' }}>Total</td>
                    <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 800, color: '#0f172a' }}>{fmt(chargeBreakdown.reduce((s, c) => s + (c.average_monthly || 0), 0))}</td>
                    <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 800, color: '#0f172a' }}>{fmt(chargeBreakdown.reduce((s, c) => s + (c.projected_total || 0), 0))}</td>

                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* ── Monthly Detail Tab ── */}
        {activeTab === 'months' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Month-by-Month Breakdown</div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Month</th>
                    <th style={{ textAlign: 'center', padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 700, color: '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Net Amount (USD)</th>

                    {/* Per-charge-type columns */}
                    {Object.keys(CHARGE_TYPE_LABELS).map(ct => (
                      <th key={ct} style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 700, color: CHARGE_TYPE_LABELS[ct].color, fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{ct} (USD)</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {months.map((m, i) => {
                    const statusColor = m.status === 'actual' ? '#2563eb' : m.status === 'projected' ? '#d97706' : '#94a3b8';
                    const statusBg = m.status === 'actual' ? '#eff6ff' : m.status === 'projected' ? '#fffbeb' : '#f8fafc';
                    const chargeMap = {};
                    (m.charge_type_breakdown || []).forEach(c => { chargeMap[c.charge_type] = c.amount; });
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: m.status === 'projected' ? '#fffbeb08' : 'transparent' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#334155' }}>{m.label}</td>
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                          <span style={{ fontSize: '9px', fontWeight: 700, color: statusColor, background: statusBg, padding: '2px 8px', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {m.status === 'no_invoice' ? 'No Invoice' : m.status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: '#0f172a' }}>{fmt(m.amount)}</td>

                        {Object.keys(CHARGE_TYPE_LABELS).map(ct => (
                          <td key={ct} style={{ textAlign: 'right', padding: '8px 8px', fontSize: '11px', fontWeight: 500, color: (chargeMap[ct] || 0) < 0 ? '#dc2626' : '#475569' }}>
                            {chargeMap[ct] !== undefined ? fmt(chargeMap[ct]) : '-'}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 800, color: '#0f172a' }}>Total</td>
                    <td></td>
                    <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 800, color: '#0f172a' }}>{fmt(data.full_year_forecast)}</td>

                    {Object.keys(CHARGE_TYPE_LABELS).map(ct => {
                      const total = months.reduce((s, m) => {
                        const found = (m.charge_type_breakdown || []).find(c => c.charge_type === ct);
                        return s + (found ? found.amount : 0);
                      }, 0);
                      return <td key={ct} style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 700, fontSize: '11px', color: total < 0 ? '#dc2626' : '#334155' }}>{fmt(total)}</td>;
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

// ─── Main Component ──────────────────────────────────────
const ProjectedInvoice = ({ formatCurrency, animated = true }) => {
  const [selectedYear, setSelectedYear] = useState(2026);
  const [detailOpen, setDetailOpen] = useState(false);
  const { data: forecastData, isLoading } = useQuery({
    queryKey: ["dashboardForecast", selectedYear],
    queryFn: fetchForecast,
    staleTime: 10 * 60 * 1000,
  });

  const forcasting = forecastData || {};
  const availableYears = forcasting?.available_years || [2026, 2025, 2024];
  return (
    <div style={{ minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className={`fd-finance-card ${animated ? 'fd-animated' : ''}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '12px' }}>
        <div className="fd-card-header">
          <h3 className="fd-card-title">Projected Invoice</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(Number(e.target.value))} 
              style={{ 
                padding: '2px 8px', 
                borderRadius: '4px', 
                border: '1px solid #e2e8f0', 
                fontSize: '12px', 
                outline: 'none', 
                color: '#475569',
                backgroundColor: '#f8fafc',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              {availableYears.map(year => (
                <option key={year} value={year}>FY {year}</option>
              ))}
            </select>
            <div className="fd-card-icon fd-icon-danger">
              <BarChart3 size={18} />
            </div>
          </div>
        </div>
        <Tooltip
          title={
            <div style={{ padding: '4px', maxWidth: '250px' }}>
              <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                USD {Number(forcasting?.average_monthly || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                Your predicted average monthly bill based on past spending.
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                To verify: This is the sum of 'Total USD' for all invoices from the last {forcasting?.actual_months || 0} months, divided by the number of unique calendar months in that period.
              </div>
            </div>
          }
          arrow
          placement="top"
        >
          <div className="fd-kpi-value" style={{ cursor: 'help', display: 'inline-block', fontSize: '24px', alignSelf: 'flex-start' }}>{isLoading ? '...' : `USD ${fmtShort(forcasting?.average_monthly || 0)}`}</div>
        </Tooltip>
        <div className="fd-kpi-label">Average monthly projection based on historical data</div>
        
        <div className="fd-trend-indicator fd-trend-up" style={{ marginBottom: '2px', marginTop: '6px', padding: '4px 8px', fontSize: '12px', alignSelf: 'flex-start' }}>
          <TrendingUp size={14} />
          <span>
            {forcasting?.label ? `${forcasting.label}` : `Based on ${forcasting?.months?.length || 0} months`}
          </span>
        </div>
        
          <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px', background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Full Year Forecast</span>
              <Tooltip
                title={
                  <div style={{ padding: '4px', maxWidth: '250px' }}>
                    <div style={{ marginBottom: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                      USD {Number(forcasting?.full_year_forecast || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontSize: '12px' }}>
                      Total cumulative predicted spend over the analyzed period.
                    </div>
                  </div>
                }
                arrow
                placement="top"
              >
                <span style={{ cursor: 'help', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                  {`USD ${fmtShort(forcasting?.full_year_forecast || 0)}`}
                </span>
              </Tooltip>
            </div>

            {!isLoading && forcasting?.actual_total !== undefined && forcasting?.projected_total !== undefined && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <span style={{ color: '#2563eb' }}>● Actual ({forcasting.actual_months}M)</span>
                  <span style={{ color: '#f59e0b' }}>● Projected ({forcasting.projected_months}M)</span>
                </div>
                
                <div style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', backgroundColor: '#e2e8f0' }}>
                  <Tooltip 
                    title={
                      <div style={{ padding: '4px', minWidth: '160px' }}>
                        <div style={{ fontSize: '11px', color: '#93c5fd', fontWeight: 700, marginBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: '4px' }}>ACTUAL SPEND ({forcasting.actual_months} Months)</div>
                        {forcasting.months?.filter(m => m.status === 'actual').map((m, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '2px 0', gap: '12px' }}>
                            <span style={{ color: 'rgba(255,255,255,0.8)' }}>{m.label}</span>
                            <span style={{ fontWeight: 600, color: '#60a5fa' }}>USD {Number(m.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0 0', marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.15)', fontWeight: 700 }}>
                          <span style={{ color: '#93c5fd' }}>Total</span>
                          <span style={{ color: '#60a5fa' }}>USD {Number(forcasting.actual_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    } 
                    arrow 
                    placement="top"
                  >
                    <div style={{ width: `${(forcasting.actual_total / forcasting.full_year_forecast) * 100}%`, backgroundColor: '#3b82f6', cursor: 'help', transition: 'width 1s ease-in-out' }}></div>
                  </Tooltip>
                  <Tooltip 
                    title={
                      <div style={{ padding: '4px', minWidth: '160px' }}>
                        <div style={{ fontSize: '11px', color: '#fcd34d', fontWeight: 700, marginBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: '4px' }}>PROJECTED SPEND ({forcasting.projected_months} Months)</div>
                        {forcasting.months?.filter(m => m.status === 'projected').map((m, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '2px 0', gap: '12px' }}>
                            <span style={{ color: 'rgba(255,255,255,0.8)' }}>{m.label}</span>
                            <span style={{ fontWeight: 600, color: '#fbbf24' }}>USD {Number(m.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0 0', marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.15)', fontWeight: 700 }}>
                          <span style={{ color: '#fcd34d' }}>Total</span>
                          <span style={{ color: '#fbbf24' }}>USD {Number(forcasting.projected_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    } 
                    arrow 
                    placement="top"
                  >
                    <div style={{ width: `${(forcasting.projected_total / forcasting.full_year_forecast) * 100}%`, backgroundColor: '#f59e0b', cursor: 'help', transition: 'width 1s ease-in-out' }}></div>
                  </Tooltip>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '600' }}>
                  <span style={{ color: '#2563eb' }}>USD {fmtShort(forcasting.actual_total)}</span>
                  <span style={{ color: '#d97706' }}>USD {fmtShort(forcasting.projected_total)}</span>
                </div>
              </>
            )}
          </div>

          {/* View Details Button */}
          {!isLoading && forcasting?.months && (
            <div className="fd-card-footer" style={{ marginTop: 'auto', paddingTop: '8px' }}>
              <button
                type="button"
                className="fd-view-action fd-view-action--small"
                onClick={() => setDetailOpen(true)}
              >
                View Details
                <ArrowRight size={11} className='fd-arrow-icon'/>
              </button>
            </div>
          )}

      </div>

      {/* Detail Dialog */}
      <ForecastDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        data={forcasting}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default ProjectedInvoice;
